import { createContext, useCallback, useContext, useMemo, type PropsWithChildren } from "react";

import { useCurrentUser } from "../hooks/useCurrentUser";
import { useLocalStorageState } from "../hooks/useLocalStorageState";
import { ACTIVE_THEME_VARIANT, type ThemeVariant } from "../theme";

type PaletteVariantContextValue = {
  /** The palette the app should render. Customers are always "classic". */
  variant: ThemeVariant;
  /** Persist a new variant (no-op for non-staff). */
  setVariant: (variant: ThemeVariant) => void;
  /** Whether the current user may switch palettes (staff only). */
  canSwitch: boolean;
};

const PaletteVariantContext = createContext<PaletteVariantContextValue>({
  variant: ACTIVE_THEME_VARIANT,
  setVariant: () => {},
  canSwitch: false,
});

const STORAGE_KEY = "encamp-palette-variant";

/**
 * Owns the active theme palette variant.
 *
 * Customers are locked to the legacy ("classic") theme; staff can opt into the
 * 2/26 brand refresh ("brand2026") Preview, persisted to localStorage. When a
 * staff user emulates a customer, `isStaff` is false, so they correctly see the
 * customer's classic theme.
 *
 * Must sit INSIDE CurrentUserProvider (needs `isStaff`) and OUTSIDE
 * EncampThemeProvider (which consumes this to build the MUI theme).
 */
export function PaletteVariantProvider({ children }: PropsWithChildren) {
  const { isStaff } = useCurrentUser();
  const [stored, setStored] = useLocalStorageState<ThemeVariant>(STORAGE_KEY, {
    defaultValue: "classic",
  });

  const variant: ThemeVariant = isStaff && stored === "brand2026" ? "brand2026" : "classic";

  const setVariant = useCallback(
    (next: ThemeVariant) => {
      if (isStaff) {
        setStored(next);
      }
    },
    [isStaff, setStored]
  );

  const value = useMemo<PaletteVariantContextValue>(
    () => ({ variant, setVariant, canSwitch: Boolean(isStaff) }),
    [variant, setVariant, isStaff]
  );

  return <PaletteVariantContext.Provider value={value}>{children}</PaletteVariantContext.Provider>;
}

export function usePaletteVariant(): PaletteVariantContextValue {
  return useContext(PaletteVariantContext);
}
